import { FC, useRef } from "react";

import Button from "../UI/Button";
import InlineAppLoading from "../UI/InlineAppLoading";

import classes from "./GallerySlot.module.css";
import { Link, useNavigate } from "react-router-dom";
import { getLang, i18n } from "../../i18n";

interface Props {
  slot: "free" | "votes" | "battles" | "nfts" | "invites" | "paid";
  title: string;
  description: string;
  level?: number;
  image?: string;
  total?: number;
  unlocked?: boolean;
  current?: number | null;
  price?: number;
  taskTitle?: string;
  loading: boolean;
  profit?: number;
  value?: number;
  unoccupied?: boolean;
  uploadFile: (
    e: React.ChangeEvent<HTMLInputElement>,
    slot: "free" | "votes" | "battles" | "nfts" | "invites" | "paid"
  ) => void;
}

const GallerySlot: FC<Props> = ({
  slot,
  title,
  description,
  level = 1,
  image,
  total,
  unlocked = false,
  current = null,
  price,
  taskTitle,
  loading = false,
  profit,
  value = 0,
  unoccupied = false,
  uploadFile,
}) => {
  const lang = getLang();
  const t = i18n.gallerySlot[lang];

  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  let stars = new Array(level);
  for (let i = 0; i < level; i++) {
    stars.push(true);
  }
  for (let i = level; i < 7; i++) {
    stars.push(false);
  }
  if (loading) {
    return (
      <div className={`${classes.gallerySlot}`}>
        <div className={classes.loading}>
          <InlineAppLoading />
        </div>
      </div>
    );
  }
  return (
    <div
      className={`${classes.gallerySlot} ${unlocked ? classes.unlocked : ""} ${
        image ? classes.maxLevel : ""
      }`}
    >
      <input
        ref={fileRef}
        type="file"
        accept=".png,.jpg,.gif,.jpeg,.webp,.avif"
        className={classes.file}
        onChange={(e) => uploadFile(e, slot)}
      />

      {image && (
        <Link to={`/image?slot=${slot}`} className={classes.image}>
          <img src={image} alt="" />
        </Link>
      )}

      {!image && (
        <div
          className={`${classes.noImage} ${
            level === 0 && !unlocked ? classes.closed : ""
          }`}
          onClick={() => unlocked && fileRef.current?.click()}
        >
          +
        </div>
      )}

      <div className={classes.title}>
        {unlocked ? (image ? title : t.upload) : t.locked}
      </div>

      {(unlocked || image) && (
        <div className={classes.level}>
          <div className={classes.stars}>
            {stars.map((star) => (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 27 27"
                fill="none"
              >
                <path
                  d="M8.53379 17.014L7.67934 21.9738C7.67934 22.4869 7.67934 23 8.53379 23C8.83874 23 12.6351 20.7766 12.9769 20.6056C13.7972 20.1951 14.6858 20.4346 15.0276 20.6056C15.3694 20.7766 19.1657 23 19.4707 23C20.3251 23 20.3251 22.4869 20.3251 21.9738L19.4707 17.014C19.4707 16.8429 19.2998 16.3299 19.8125 15.8168L23.572 12.0541C23.9708 11.598 24.4265 10.8569 23.2302 10.6859L17.7572 9.83074C17.0737 9.65971 16.7319 8.97559 16.561 8.63354L14.6812 4.52883C14.1344 3.57107 13.542 4.12976 13.3141 4.52883L11.2635 8.63354C11.0926 8.97559 10.9217 9.65971 10.2381 9.83074L4.76975 10.6859C3.57353 10.8569 4.02923 11.598 4.42798 12.0541L8.18754 15.8168C8.7002 16.3299 8.53379 16.8429 8.53379 17.014Z"
                  fill={star ? "#3095dd" : image ? "#fff" : "#d2e9fb"}
                />
              </svg>
            ))}
          </div>

          {total && (
            <div className={classes.progress}>
              {current}/{total}
            </div>
          )}
        </div>
      )}

      {!image && <div className={classes.description}>{description}</div>}

      {image && (
        <div className={classes.profit}>
          <div className={classes.profitTitle}>{t.value}</div>
          <div className={`${classes.count}`}>{Math.round(value)} 💎</div>
        </div>
      )}

      {!unlocked && !image && (
        <div className={classes.level}>
          <div className={classes.slotLevel}>{taskTitle}</div>
          {total && (
            <div className={classes.progress}>
              {current}
              {slot === "votes" && taskTitle === "Expert Rate" && "%"}/{total}
              {slot === "votes" && taskTitle === "Expert Rate" && "%"}
            </div>
          )}
        </div>
      )}

      <div
        className={`${classes.bar} ${
          !image && !unlocked && current === null ? classes.barNull : ""
        }`}
      >
        {current !== null && total && (
          <div className={classes.line}>
            <div
              className={classes.filled}
              style={{
                width: `${((current < 0 ? 0 : current) / total) * 100}%`,
              }}
            ></div>
          </div>
        )}
      </div>

      <div className={classes.btn}>
        {unlocked && !image && (
          <Button
            onClick={() => fileRef.current?.click()}
            small
            important
            label={t.uploadArt}
          />
        )}

        {unlocked && image && (
          <Button
            onClick={() => navigate(`/battle?slot=${slot}`)}
            small
            label={t.sendToBattle}
            shimmer={unoccupied}
          />
        )}

        {!unlocked && slot === "paid" && (
          <Button
            disabled
            small
            href="#"
            label={`${t.unlockPaid} (${price} TG Stars)`}
          />
        )}

        {!unlocked && slot !== "paid" && (
          <Button disabled small href="#" label={t.locked} />
        )}
      </div>
    </div>
  );
};

export default GallerySlot;
